<template>
  <!-- Вкладка «Отзывы»: сводка (крупное среднее, звёзды, распределение),
       лента отзывов и призыв «Прошли этот маршрут? - Оставить отзыв» (вход -
       через loginGate; владельцу свой маршрут оценивать нельзя). -->
  <section class="px-4 pb-6" data-route-reviews>
    <div v-if="data && data.count" class="flex items-center gap-4 pb-4">
      <div class="shrink-0 text-center">
        <p class="num display text-[44px] leading-none">{{ average }}</p>
        <StarRating class="mt-1.5" :value="data.average" :size="14" />
        <p class="mt-1 text-[12px] text-muted-foreground">{{ $t('reviews.count', { n: data.count }) }}</p>
      </div>
      <div class="min-w-0 flex-1 space-y-1" aria-hidden="true">
        <div v-for="n in [5, 4, 3, 2, 1]" :key="n" class="flex items-center gap-2">
          <span class="num w-3 text-right text-[11px] text-muted-foreground">{{ n }}</span>
          <div class="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
            <div class="h-full rounded-full bg-amber-500" :style="{ width: `${(data.distribution[String(n)] / data.count) * 100}%` }" />
          </div>
        </div>
      </div>
    </div>

    <ul v-if="data && data.items.length" class="divide-y divide-border border-t border-border">
      <li v-for="(item, i) in shownItems" :key="i" data-review-item class="flex gap-3 py-3.5">
        <span class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[14px] font-semibold text-primary" aria-hidden="true">{{ initial(item.author) }}</span>
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="truncate text-[14px] font-semibold">{{ item.author }}</span>
            <span class="ml-auto shrink-0 text-[12px] text-muted-foreground">{{ formatDate(item.created_at) }}</span>
          </div>
          <StarRating class="mt-0.5" :value="item.stars" :size="13" />
          <p v-if="item.comment" class="mt-1.5 whitespace-pre-line break-words text-[14px] leading-snug">{{ item.comment }}</p>
        </div>
      </li>
    </ul>
    <button v-if="data && data.items.length > PREVIEW && !showAll" type="button" class="mt-1 text-[13px] font-semibold text-primary" @click="showAll = true">
      {{ $t('reviews.showMore') }}
    </button>

    <div v-if="!isOwner" class="mt-4 rounded-xl border border-border bg-muted/40 p-4 text-center">
      <p class="text-[14px] font-semibold">{{ data?.mine ? $t('reviews.mineTitle') : $t('reviews.ctaTitle') }}</p>
      <p class="mt-0.5 text-[13px] text-muted-foreground leading-snug">{{ data?.mine ? $t('reviews.mineText') : $t('reviews.ctaText') }}</p>
      <button type="button" class="btn btn--primary mt-3 w-full justify-center" @click="openDialog">
        <Star class="h-4 w-4" aria-hidden="true" />{{ data?.mine ? $t('reviews.edit') : $t('reviews.cta') }}
      </button>
    </div>
  </section>

  <ReviewDialog
    v-if="dialog"
    :route-id="routeId"
    :route-name="routeName"
    :existing="data?.mine ?? null"
    @close="dialog = false"
    @saved="onSaved"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Star } from "lucide-vue-next";
import api from "../../api";
import { useAuthStore } from "../../store/auth";
import { useLoginGateStore } from "../../store/loginGate";
import type { ReviewsResponse } from "../../types/reviews";
import ReviewDialog from "./ReviewDialog.vue";
import StarRating from "./StarRating.vue";

const props = defineProps<{ routeId: number; routeName: string; ownerUserId?: number | null }>();
const emit = defineEmits<{ (e: "rated"): void }>();

const PREVIEW = 3;
const auth = useAuthStore();
const loginGate = useLoginGateStore();
const data = ref<ReviewsResponse | null>(null);
const dialog = ref(false);
const showAll = ref(false);

const isOwner = computed(() => auth.userId != null && props.ownerUserId === auth.userId);
const average = computed(() => (data.value?.average != null ? data.value.average.toFixed(1) : ""));
const shownItems = computed(() => (showAll.value ? data.value?.items ?? [] : (data.value?.items ?? []).slice(0, PREVIEW)));
const initial = (name: string) => (name.trim()[0] ?? "?").toUpperCase();
const formatDate = (iso: string) => new Date(iso).toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "numeric" });

const load = async () => {
  showAll.value = false;
  try {
    const res = (await api.get<ReviewsResponse>(`/routes/${props.routeId}/reviews`, { params: { limit: 50 } })).data;
    data.value = Array.isArray(res?.items) ? res : null;
  } catch {
    data.value = null;
  }
};
watch(() => [props.routeId, auth.userId], load, { immediate: true });

const openDialog = () => loginGate.requireLogin(auth.isAuthenticated, () => (dialog.value = true));
const onSaved = (next: ReviewsResponse) => {
  data.value = next;
  emit("rated");
};
</script>
